/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/
import { DoublyLinkedNode } from "./DoublyLinkedNode";

export class DoublyLinkedList<T> {
  private _head?: DoublyLinkedNode<T>;
  private _tail?: DoublyLinkedNode<T>;

  /** The current head of the list */
  public get head() {
    return this._head;
  }

  /** The current tail of the list */
  public get tail() {
    return this._tail;
  }

  /** The current length of the list */
  public length: number;

  public constructor() {
    this.length = 0;
  }

  /** Adds a new node to the end of the list */
  public push(val: T) {
    const newNode = new DoublyLinkedNode(val);
    if (!this._head) {
      this._head = newNode;
      this._tail = newNode;
    } else {
      this._tail!.next = newNode;
      newNode.prev = this._tail;
      this._tail = newNode;
    }
    this.length++;
    return this;
  }

  /** Removes and returns a node from the end of the list or false if list is empty */
  public pop() {
    if (this.length === 0 || !this._tail) {
      return false;
    }
    const popped = this._tail;
    const new_tail = this._tail.prev;
    if (new_tail) {
      new_tail.next = undefined;
      this._tail.prev = undefined;
    } else {
      this._head = undefined;
    }
    this._tail = new_tail;
    this.length--;

    return popped;
  }

  /** Removes and returns a node from the start of the list or false if list is empty */
  public shift() {
    if (!this._head) {
      return false;
    }
    const shiftedNode = this._head;
    const new_head = this._head.next;
    if (new_head && this._head !== this._tail) {
      new_head.prev = undefined;
      shiftedNode.next = undefined;
    } else {
      this._tail = undefined;
    }
    this._head = new_head;
    this.length--;
    return shiftedNode;
  }

  /** Adds a node from the start of the list */
  public unshift(val: T) {
    const newNode = new DoublyLinkedNode(val);
    if (!this._head) {
      this._head = newNode;
      this._tail = newNode;
    } else {
      this._head.prev = newNode;
      newNode.next = this._head;
      this._head = newNode;
    }
    this.length++;
    return this;
  }

  /** Finds the index of the node that matches the specified condition or returns -1 */
  public findIndex(search: (val: DoublyLinkedNode<T>) => boolean) {
    if (this.length === 0 || !this._head) {
      return -1;
    }
    let current: DoublyLinkedNode<T> | undefined = this._head;
    let index = 0;
    while (current !== undefined) {
      const found = search(current);
      if (found) {
        return index;
      }
      current = current.next;
      index++;
    }
    return -1;
  }

  /** Finds the node that matches the specified condition or returns undefined */
  public find(search: (val: DoublyLinkedNode<T>) => boolean) {
    if (this.length === 0 || !this._head) {
      return undefined;
    }
    let current: DoublyLinkedNode<T> | undefined = this._head;
    while (current !== undefined) {
      if (search(current)) {
        return current;
      }
      current = current.next;
    }
    return undefined;
  }

  /** Finds the node at the specified position in the list or returns undefined */
  public at(index: number) {
    return [...this][index]
  }

  /** Loops through the entire list and calls the callback for each node in the list */
  public forEach(callbackfn: (value: DoublyLinkedNode<T>, index: number, array: DoublyLinkedNode<T>[]) => void, thisArg?: any) {
    [...this].forEach(callbackfn, thisArg);
  }

  /** Returns a list of nodes that match the specified condition */
  public filter(callbackfn: (value: DoublyLinkedNode<T>, index: number, array: DoublyLinkedNode<T>[]) => DoublyLinkedNode<T>[], thisArg?: any) {
    return [...this].filter(callbackfn, thisArg);
  }

  /** Returns a list of type U after interating through the list of nodes */
  public map<U>(callbackfn: (value: DoublyLinkedNode<T>, index: number, array: DoublyLinkedNode<T>[]) => U, thisArg?: any) {
    return [...this].map(callbackfn, thisArg);
  }

  /** Returns whter the specified node exists in the list */
  public includes(value: DoublyLinkedNode<T>) {
    return [...this].includes(value);
  }

  public *[Symbol.iterator]() {
    let node = this._head;
    while (node) {
      yield node;
      node = node.next;
    }
  }
}